import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, EffectFade } from "swiper/modules";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-fade";

const productSlides = [
  {
    id: "school-management",
    title: "School Management System",
    description:
      "Streamline administrative tasks for educational institutions. Our SMS manages student data, records, and finance with total transparency.",
    details: [
      "Student Information Management",
      "Automated Fee Collection & Invoicing",
      "Examination & Result Processing",
      "Parent-Teacher Communication Portal",
    ],
    img: "/products/schoolmag.png",
  },
  {
    id: "hotel-management",
    title: "Hotel Management System",
    description:
      "Optimize day-to-day hospitality operations. From front desk tasks to revenue reporting, our HMS delivers exceptional guest service efficiency.",
    details: [
      "Real-time Room Booking Engine",
      "Housekeeping & Staff Management",
      "POS Integration for Restaurants",
      "Night Audit & Financial Reporting",
    ],
    img: "/products/hotelm.jpg",
  },
  {
    id: "clinic-management",
    title: "Clinic Management System",
    description:
      "Automate patient appointments, billing, and clinical operations. Designed to help healthcare providers focus on what matters: patient care.",
    details: [
      "Electronic Medical Records (EMR)",
      "Doctor's Schedule Management",
      "Pharmacy & Inventory Control",
      "Insurance Claim Integration",
    ],
    img: "/products/clientmg.png",
  },
  {
    id: "erp-crm",
    title: "ERP & CRM Systems",
    description:
      "Consolidate customer info, manage contacts, and track sales. Features include HR modules, billing, and project management with robust reporting.",
    details: [
      "Lead & Opportunity Tracking",
      "Supply Chain Management",
      "HR & Payroll Automation",
      "Dynamic Dashboard Analytics",
    ],
    img: "/products/crmerp.jpg",
  },
  {
    id: "cloud-pbx",
    title: "Cloud PBX",
    description:
      "A virtual phone system hosted in the cloud, allowing businesses to manage communication needs over the internet without traditional hardware. Perfect for remote or distributed teams.",
    details: [
      "Scalable & Cost-effective Solution",
      "Internal & External Call Management",
      "Remote Team Accessibility",
      "Hardware-free Setup",
    ],
    img: "/products/cloudpbx.png",
  },
  {
    id: "sms-gateway",
    title: "SMS Gateway",
    description:
      "A bridge between messaging platforms that enables sending and receiving messages across different Mobile Operators like ATOM, Ooredoo, MPT, and Mytel.",
    details: [
      "Marketing, Notifications & Auth",
      "Sender ID Customization",
      "Message Queuing & Delivery Reports",
      "Two-way Messaging & Security",
    ],
    img: "/products/smsgate.png",
  },
  {
    id: "payment-gateway",
    title: "Payment Gateway",
    description:
      "Securely connects merchant websites to payment processors or banks. It encrypts transaction info, supporting methods like KBZPay, WavePay, Visa, and MPU.",
    details: [
      "Secure Online Transactions",
      "MMQR & Local Bank Integration",
      "International Card Support (Visa/JCB)",
      "Encrypted Data Compliance",
    ],
    img: "/products/paymentgate.png",
  },
  {
    id: "marketing-tools",
    title: "Marketing Tools",
    description:
      "Powerful platforms designed to help businesses connect with audiences efficiently through automated SMS and email communication channels.",
    details: [
      "Campaign Scheduling & Management",
      "Transactional Message Automation",
      "Multi-channel (SMS & Email)",
      "Audience Engagement Analytics",
    ],
    img: "/products/marketingtools.png",
  },
  {
    id: "top-up-gateway",
    title: "Top-up Gateway",
    description:
      "A dedicated API-driven service designed to process mobile airtime recharges and data pack purchases for all major telecommunication operators in Myanmar, including ATOM, Ooredoo, MPT, and Mytel.",
    details: [
      "Real-time Mobile Airtime Recharges",
      "Data Pack Purchase Processing",
      "Supports E-Load & E-Pin Methods",
      "Uninterrupted API-driven Integration",
    ],
    img: "/services/topupgate.jpg",
  },
];

export default function Swiperone() {
  const swiperRef = useRef();

  return (
    <div className="relative group/swiper">
      <Swiper
        loop={true}
        effect={"fade"}
        fadeEffect={{ crossFade: true }}
        modules={[Navigation, Autoplay, EffectFade]}
        autoplay={{ delay: 4000, disableOnInteraction: false }}
        onBeforeInit={(swiper) => (swiperRef.current = swiper)}
        className="mySwiperone text-white">
        {productSlides.map((product, index) => (
          <SwiperSlide key={index}>
            <div className="lg:flex items-center gap-10 py-6">
              <div className="lg:w-1/2">
                <motion.div className="relative group">
                  <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 to-emerald-400 rounded-3xl blur-xl opacity-10 group-hover:opacity-20 transition" />
                  <div className="relative bg-white/5 border border-white/10 backdrop-blur-xl rounded-[28px] p-5 shadow-2xl">
                    <img
                      className="max-w-full rounded-3xl max-h-full w-auto h-auto object-contain drop-shadow-2xl transition-transform duration-500 group-hover:scale-105"
                      src={product.img}
                      alt={product.title}
                    />
                  </div>
                </motion.div>
              </div>

              <div className="lg:w-1/2 text-start mt-8 lg:mt-0">
                <motion.div>
                  <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-[#007BFF] to-[#05DFAD] text-white uppercase tracking-[0.2em] mb-6">
                    Our Products
                  </span>
                  <h3 className="text-3xl md:text-4xl font-bold mb-6 text-white leading-tight">
                    {product.title}
                  </h3>
                  <p className="text-slate-400 text-base md:text-lg leading-relaxed max-w-xl">
                    {product.description}
                  </p>

                  <Link to={`/products/${product.id}`}>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="mt-8 px-8 py-3 bg-gradient-to-r from-[#007BFF] to-[#05DFAD] rounded-xl text-white font-bold uppercase tracking-widest text-sm hover:opacity-90 transition-opacity shadow-lg">
                      Learn More
                    </motion.button>
                  </Link>
                </motion.div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="flex justify-end gap-3 mt-8">
        <button
          onClick={() => swiperRef.current?.slidePrev()}
          className="w-12 h-12 rounded-full border border-slate-700 flex items-center justify-center text-white hover:border-[#007BFF] hover:bg-slate-800 transition-all active:scale-90">
          <ArrowLeft size={20} />
        </button>
        <button
          onClick={() => swiperRef.current?.slideNext()}
          className="w-12 h-12 rounded-full bg-gradient-to-r from-[#007BFF] to-[#05DFAD] flex items-center justify-center text-white shadow-lg hover:shadow-blue-500/20 transition-all active:scale-90">
          <ArrowRight size={20} />
        </button>
      </div>
    </div>
  );
}
